"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { GoogleIcon } from "@/components/Icons";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Button } from "@/components/ui/design-system/button";
import { CodeField } from "@/components/ui/design-system/code-field";
import { Input } from "@/components/ui/design-system/input";
import { Label } from "@/components/ui/design-system/label";
import { Body, Display } from "@/components/ui/design-system/text";
import { sharedAuthClient } from "@/lib/sharedAuthClient";
import { cn } from "@/lib/utils";
import { AdRevealBackground } from "./adReveal/AdRevealBackground";
import { CreatorAuthFrame } from "./CreatorAuthFrame";

/**
 * The six digits, as a constant rather than three literals.
 */
const CODE_LENGTH = 6;

/** A text action: the resend and the way back. */
const LINK = cn(
  "inline-flex cursor-pointer items-center gap-nf-ds-2 rounded-nf-ds-button",
  "font-nf-ds-sans text-nf-ds-sm font-medium",
  "outline-hidden transition-colors duration-150 ease-nf-ds-out",
  "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
  "disabled:pointer-events-none disabled:opacity-50",
  "active:opacity-70 motion-reduce:transition-none",
);

export function CreatorSignIn({
  initialEmail = "",
  legacyLink,
  googleEnabled,
  onSignedIn,
}: {
  initialEmail?: string;
  legacyLink: boolean;
  googleEnabled: boolean;
  onSignedIn: () => Promise<void>;
}) {
  const t = useTranslations("signIn");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  /**
   * Bumped on every rejected code, to remount the field.
   *
   * Clearing the value empties the cells but leaves the caret wherever it
   * was, which on a rejection is the last one — so the reader starts typing
   * into the sixth box. Remounting re-runs the field's own autofocus and puts
   * them back at the first.
   */
  const [attempt, setAttempt] = useState(0);
  /** The block the ad wall dims behind, so the copy stays readable. */
  const guardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const callback = new URL(window.location.href);
    if (!callback.searchParams.has("error")) return;
    setError(t("errors.googleFinish"));
    callback.searchParams.delete("error");
    window.history.replaceState(window.history.state, "", callback);
  }, []);
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function sendCode() {
    setPending(true);
    setError(null);
    try {
      const result = await sharedAuthClient.emailOtp.sendVerificationOtp({
        email: email.trim().toLowerCase(),
        type: "sign-in",
      });
      if (result.error) {
        setError(result.error.message ?? t("errors.sendFailed"));
        return;
      }
      setRequested(true);
      setCode("");
      setCooldown(60);
    } catch {
      setError(t("errors.offline"));
    } finally {
      setPending(false);
    }
  }
  /**
   * `otp` defaults to state for the button path, and is passed explicitly by
   * the auto-submit. `onComplete` fires from inside the field's write, before
   * React has re-rendered, so reading `code` from the closure there sends the
   * value from *before* the last digit — an empty string on the first try.
   */
  async function verify(otp: string = code) {
    setPending(true);
    setError(null);
    try {
      const result = await sharedAuthClient.signIn.emailOtp({
        email: email.trim().toLowerCase(),
        otp,
      });
      if (result.error) {
        setError(result.error.message ?? t("errors.codeRejected"));
        // Empty the cells. Six digits already rejected are six digits to
        // delete before the next attempt, and leaving them there invites
        // re-submitting the same code.
        setCode("");
        setAttempt((n) => n + 1);
        return;
      }
      await onSignedIn();
    } catch {
      setError(t("errors.verifyFailed"));
      setCode("");
      setAttempt((n) => n + 1);
    } finally {
      setPending(false);
    }
  }
  async function google() {
    setPending(true);
    setError(null);
    try {
      const callback = new URL(window.location.href);
      callback.searchParams.delete("error");
      const result = await sharedAuthClient.signIn.social({
        provider: "google",
        callbackURL: callback.toString(),
        errorCallbackURL: callback.toString(),
      });
      if (result.error)
        setError(result.error.message ?? t("errors.googleStart"));
    } catch {
      setError(t("errors.googleStart"));
    } finally {
      setPending(false);
    }
  }

  return (
    <CreatorAuthFrame background={<AdRevealBackground guardRef={guardRef} />}>
      {/*
        The guard goes on the block that hugs the content, not on the column
        that centres it. That column is `flex-1`, so it is as tall as the
        page — and the ad wall sizes both its readability damping and its
        idle orbit from this rect, so measuring the stretched parent put the
        orbit's radius past the top and bottom of the screen and dimmed most
        of it.
      */}
      <div ref={guardRef} className="flex flex-col gap-nf-ds-6">
        <header className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center">
          {/* Brand, not metadata — the one place on this screen where colour
              is the point rather than decoration. */}
          <Display as="h1" className="text-balance">
            {requested
              ? t("checkEmail")
              : t.rich("headline", {
                  highlight: (chunks) => (
                    <span className="bg-[image:var(--nf-ds-brand-grad)] bg-clip-text text-transparent">
                      {chunks}
                    </span>
                  ),
                })}
          </Display>
          <Body muted className="text-pretty">
            {requested
              ? t.rich("codeSent", {
                  length: CODE_LENGTH,
                  email: email.trim(),
                  emphasis: (chunks) => (
                    <span className="text-nf-ds-fg">{chunks}</span>
                  ),
                })
              : t("lede")}
          </Body>
        </header>

        {legacyLink && (
          <Alert>
            <AlertDescription>{t("legacyLink")}</AlertDescription>
          </Alert>
        )}

        <form
          className="flex w-full flex-col items-center gap-nf-ds-4"
          onSubmit={(event) => {
            event.preventDefault();
            void (requested ? verify() : sendCode());
          }}
        >
          {requested ? (
            <>
              <div className="flex w-full flex-col gap-nf-ds-2">
                {/* A span, not a label: six inputs have no single target for
                    `htmlFor`, and CodeField's fieldset already carries the
                    group's accessible name. `asChild` keeps the typography
                    without the dangling element. */}
                <Label asChild>
                  <span>{t("codeLabel")}</span>
                </Label>
                <CodeField
                  key={attempt}
                  id="creator-code"
                  value={code}
                  onChange={(next) => {
                    setCode(next);
                    // Clear the rejection as soon as the reader edits. A code
                    // marked wrong while they fix it is a stale accusation.
                    if (error) setError(null);
                  }}
                  length={CODE_LENGTH}
                  autoFocus
                  invalid={Boolean(error)}
                  describedBy="creator-code-hint"
                  // The sixth digit submits. Asking someone to confirm a
                  // code they have just finished typing is a step that
                  // exists only because the form has a button. A rejection
                  // lands where it would have anyway, and editing a digit
                  // clears it and arms the next attempt.
                  //
                  // Guarded on `pending` so a paste landing mid-request
                  // cannot fire a second verify.
                  onComplete={(full) => {
                    if (pending) return;
                    void verify(full);
                  }}
                />
                <div
                  id="creator-code-hint"
                  className="flex min-h-nf-ds-8 items-center justify-center"
                >
                  {pending ? (
                    <Body muted className="text-nf-ds-sm">
                      {t("signingIn")}
                    </Body>
                  ) : cooldown > 0 ? (
                    <Body muted className="text-nf-ds-sm">
                      {t("resendCountdown", { seconds: cooldown })}
                    </Body>
                  ) : (
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => void sendCode()}
                      className={cn(
                        LINK,
                        "px-nf-ds-2 text-nf-ds-fg underline decoration-nf-ds-fg/30 underline-offset-2",
                      )}
                    >
                      {t("resend")}
                    </button>
                  )}
                </div>
              </div>

              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setRequested(false);
                  setCode("");
                  setError(null);
                }}
                className={cn(LINK, "px-nf-ds-2 text-nf-ds-fg-muted")}
              >
                <ArrowLeft aria-hidden="true" className="size-4" />
                {t("differentEmail")}
              </button>
            </>
          ) : (
            <>
              {/* The submit sits inside the field. There is exactly one thing
                  to do with an address here, so a second full-width button
                  below it earned nothing. */}
              <div className="relative w-full">
                <Label className="sr-only" htmlFor="creator-email">
                  {t("emailLabel")}
                </Label>
                <Input
                  id="creator-email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={254}
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  disabled={pending}
                  onChange={(event) => setEmail(event.target.value)}
                  className="pr-nf-ds-12"
                />
                <button
                  type="submit"
                  disabled={pending || !email.trim()}
                  aria-label={t("emailSubmit")}
                  className={cn(
                    "absolute inset-y-0 right-nf-ds-2 my-auto",
                    "inline-flex size-8 cursor-pointer items-center justify-center rounded-nf-ds-button",
                    "text-nf-ds-fg-muted outline-hidden",
                    "transition-[color,background-color] duration-150 ease-nf-ds-out",
                    "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
                    "disabled:pointer-events-none disabled:opacity-40",
                    "motion-reduce:transition-none",
                  )}
                >
                  <ArrowRight aria-hidden="true" className="size-4" />
                </button>
              </div>

              {/* Ghost, not filled. Two buttons of equal weight ask the
                  reader to choose before telling them the difference — which
                  is what retired the "or" rule — but a bare text link
                  under-sells a real alternative. A body that appears on hover
                  says "this is a control" without competing with the one
                  action the screen is for. */}
              {googleEnabled && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={pending}
                  onClick={() => void google()}
                  className="gap-nf-ds-2"
                >
                  <GoogleIcon className="size-4" />
                  {t("google")}
                </Button>
              )}

              <Body muted className="text-nf-ds-sm text-pretty">
                {t("codeHint", { length: CODE_LENGTH })}
              </Body>
            </>
          )}

          {error && (
            <Alert variant="err" role="alert" className="w-full">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </form>
      </div>
    </CreatorAuthFrame>
  );
}
